import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Button } from '@mui/material';

import { Dialog } from '../components';
import { translate } from '../utils/translations';

type ConfirmOptions = {
  message: string;
};

type ConfirmContextValue = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

type ConfirmRequest = {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
};

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function ConfirmProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const [req, setReq] = useState<ConfirmRequest | null>(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setReq({ options, resolve });
      }),
    []
  );

  const close = (result: boolean) => {
    if (!req) return;
    req.resolve(result);
    setReq(null);
  };

  const value = useMemo<ConfirmContextValue>(
    () => ({
      confirm,
    }),
    [confirm]
  );

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog
        open={req !== null}
        content={req?.options.message}
        onClose={() => close(false)}
        width="800px"
        actions={[
          <Button
            key="cancel"
            color="error"
            variant="outlined"
            onClick={() => close(false)}
          >
            {translate('COMMON.CANCEL')}
          </Button>,
          <Button
            key="submit"
            color="primary"
            variant="contained"
            onClick={() => close(true)}
          >
            {translate('COMMON.CONFIRM')}
          </Button>,
        ]}
      />
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx.confirm;
};
